import classNames from "classnames";
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { ANIMATION_TYPE, SPOILER_TYPE } from "./../../ricos-content/index.js";
import { buildThemeCssVars } from "./../../ricos-styles/index.js";
import { hasPlugin } from "./../../_shared/has-plugin.js";
import {
  AccessibilityListener,
  GlobalContext,
  getLangDir
} from "./../../wix-rich-content-common/index.js";
import viewerStyles from "../statics/rich-content-viewer-scss.js";
import rtlStyles from "../statics/rtl-scss.js";
import { AddAnchors } from "./components/add-anchors.js";
import { ContentTraverser } from "./content-traverser.js";
import { ANIMATION_SCOPE_ATTR, NodeAnimations } from "./node-animations.js";
const styles = { ...viewerStyles, ...rtlStyles };
let animationScopeCounter = 0;
const useIdCompat = React.useId ?? function useIdFallback() {
  const ref = useRef();
  if (ref.current === void 0) {
    ref.current = `fallback${animationScopeCounter++}`;
  }
  return ref.current;
};
const ContentViewer = (props) => {
  const {
    config = {},
    onHover,
    isMobile = false,
    addAnchors,
    onMouseOver = () => {
    },
    content,
    setRef,
    typeMappers,
    nodeViewRenderers,
    nodeViewDecorators,
    experiments = {},
    t
  } = props;
  const [minHeight, setMinHeight] = useState(void 0);
  const viewerRef = useRef(null);
  const contentRef = useRef(null);
  useLayoutEffect(() => {
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    if (!isSafari || !contentRef.current) {
      return;
    }
    const resizeObserver = new ResizeObserver((entries) => {
      const newHeight = entries[0].contentRect.height;
      if (newHeight > 0) {
        setMinHeight(newHeight);
      }
    });
    resizeObserver.observe(contentRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [content]);
  const handleRef = useCallback(
    (node) => {
      viewerRef.current = node;
      if (setRef) {
        setRef.current = node;
      }
    },
    [setRef]
  );
  const SpoilerViewerWrapper = config[SPOILER_TYPE]?.SpoilerViewerWrapper;
  const contextualData = getContextualData(props);
  const animationsEnabled = hasPlugin(ANIMATION_TYPE, contextualData.plugins);
  const animationScopeId = `ricos-anim-${useIdCompat().replace(/[^a-zA-Z0-9]/g, "")}`;
  const viewerClassName = classNames(styles.viewer, {
    [rtlStyles.rtl]: contextualData.textDirection === "rtl"
  });
  const style = buildThemeCssVars(contextualData.theme, content?.documentStyle ?? {});
  const dataId = { "data-id": "content-viewer" };
  return /* @__PURE__ */ React.createElement(GlobalContext.Provider, { key: "RicosViewer", value: { experiments, isMobile, t } }, /* @__PURE__ */ React.createElement(
    "div",
    {
      className: classNames(styles.wrapper),
      dir: contextualData.languageDir,
      style: {
        ...style,
        minHeight: minHeight ? `${minHeight}px` : void 0
      },
      onMouseEnter: (e) => onHover && onHover(e),
      onMouseOver,
      ref: handleRef,
      ...dataId
    },
    props.devTools,
    /* @__PURE__ */ React.createElement(
      NodeAnimations,
      {
        content,
        contentRef,
        enabled: animationsEnabled,
        scopeId: animationScopeId
      }
    ),
    /* @__PURE__ */ React.createElement(
      "div",
      {
        className: viewerClassName,
        ref: contentRef,
        ...animationsEnabled ? { [ANIMATION_SCOPE_ATTR]: animationScopeId } : {}
      },
      /* @__PURE__ */ React.createElement(AddAnchors, { addAnchors }, /* @__PURE__ */ React.createElement(
        ContentTraverser,
        {
          content,
          typeMappers,
          context: contextualData,
          nodeViewRenderers,
          nodeViewDecorators,
          SpoilerViewerWrapper
        }
      ))
    ),
    /* @__PURE__ */ React.createElement(AccessibilityListener, { isMobile })
  ));
};
const getContextualData = ({
  t,
  theme,
  plugins,
  isMobile = false,
  anchorTarget,
  relValue,
  customAnchorScroll,
  config,
  locale,
  disabled,
  seoMode,
  iframeSandboxDomain,
  textAlignment,
  experiments,
  textDirection,
  direction,
  helpers,
  onNodeClick,
  jsdomMode,
  baseUrl,
  userDomainMedia,
  isPrint
}) => {
  return {
    t,
    theme: theme || {},
    isMobile,
    anchorTarget,
    relValue,
    customAnchorScroll,
    config,
    helpers: helpers || {},
    locale: locale || "en",
    disabled,
    seoMode: seoMode ?? (isPrint ? {} : void 0),
    iframeSandboxDomain,
    disableRightClick: config?.uiSettings?.disableRightClick,
    textAlignment,
    experiments,
    textDirection,
    languageDir: direction || getLangDir(locale),
    onNodeClick,
    jsdomMode,
    baseUrl,
    userDomainMedia,
    isPrint,
    plugins
  };
};
export {
  ContentViewer
};
//# sourceMappingURL=content-viewer.js.map
